import React, { useEffect } from 'react';
import { Navbar } from '../../../components/feature/Navbar';
import { Footer } from '../../../components/feature/Footer';

export default function BusinessWebsitePage() {
  useEffect(() => {
    document.title = 'Business Website Design Services | Professional Custom Websites | BusinessWebsitePro';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional business website design services for companies in Canada, USA, and worldwide. Custom-designed websites that showcase your brand and drive results. Get a quote today.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[75vh] max-h-[85vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://static.readdy.ai/image/afbb870fba5009b6251518206686e578/2d22fa2dd98cdcab98ef4f1ec23cc841.png"
            alt="Business website design services"
            className="w-full h-full object-cover object-top"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        {/* Navbar */}
        <div className="absolute top-0 left-0 right-0 z-30">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <a href="/" className="flex items-center gap-3 cursor-pointer">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                  <i className="ri-window-line text-2xl text-white"></i>
                </div>
                <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  BusinessWebsitePro
                </span>
              </a>
              
              <nav className="hidden md:flex items-center gap-8">
                <a href="/" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">Home</a>
                <a href="/about" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">About</a>
                <a href="/services" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">Services</a>
                <a href="/portfolio" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">Portfolio</a>
                <a href="/pricing" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">Pricing</a>
                <a href="/blog" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">Blog</a>
                <a href="/contact" className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-6 py-2 rounded-full font-semibold hover:shadow-lg transition-all cursor-pointer whitespace-nowrap">
                  Contact Us
                </a>
              </nav>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-3xl p-8 sm:p-12 shadow-2xl" style={{ marginLeft: '8rem', maxWidth: '600px' }}>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 drop-shadow-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Business Website Design Services
            </h1>
            <p className="text-xl text-white/90 mb-8 drop-shadow-lg">
              Professional custom websites designed to grow your business. Advanced SEO, Google Maps integration, and everything you need to succeed online.
            </p>
            
            <div className="flex flex-wrap gap-4 justify-end" style={{ marginTop: '150px', marginRight: '75px' }}>
              <a
                href="/booking"
                className="group relative px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line text-xl"></i>
                Book Free Consultation
              </a>
              <a
                href="/contact"
                className="group relative px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-xl border-2 border-white/30 hover:bg-white/20 hover:scale-105 transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-eye-line text-xl"></i>
                View Our Work
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content Section - Business Website Design Process */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Business Website Design Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A proven methodology that delivers exceptional results for businesses across North America and beyond
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Discovery & Strategy</h3>
              <p className="text-gray-600 leading-relaxed">
                We analyze your business goals, target audience, and competitors to create a winning website strategy that drives measurable results.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Design & Development</h3>
              <p className="text-gray-600 leading-relaxed">
                Our expert designers create custom layouts that reflect your brand while our developers build a fast, secure, and scalable website.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Content & SEO</h3>
              <p className="text-gray-600 leading-relaxed">
                We optimize every page for search engines and AI search platforms, ensuring your business gets found by customers searching online.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">4</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Launch & Support</h3>
              <p className="text-gray-600 leading-relaxed">
                After thorough testing, we launch your website and provide 60 days of support to ensure everything runs smoothly and successfully.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Industries We Serve Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Industries We Serve</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional website design solutions tailored to your industry's unique needs and customer expectations
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-restaurant-line', name: 'Restaurants & Cafes' },
              { icon: 'ri-store-line', name: 'Retail & E-commerce' },
              { icon: 'ri-building-line', name: 'Real Estate' },
              { icon: 'ri-heart-pulse-line', name: 'Healthcare & Wellness' },
              { icon: 'ri-scales-line', name: 'Legal Services' },
              { icon: 'ri-bank-line', name: 'Financial Services' },
              { icon: 'ri-hammer-line', name: 'Construction & Trades' },
              { icon: 'ri-car-line', name: 'Automotive Services' },
              { icon: 'ri-scissors-line', name: 'Salons & Spas' },
              { icon: 'ri-graduation-cap-line', name: 'Education & Training' },
              { icon: 'ri-home-smile-line', name: 'Home Services' },
              { icon: 'ri-briefcase-line', name: 'Professional Services' }
            ].map((industry, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200 hover:border-teal-500 transition-all duration-300 text-center">
                <i className={`${industry.icon} text-4xl text-teal-600 mb-3`}></i>
                <p className="text-gray-900 font-semibold">{industry.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What's Included in Business Website Design</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything your business needs to establish a strong professional online presence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl border border-blue-100">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-layout-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Custom Professional Design</h3>
              <p className="text-gray-600 leading-relaxed">
                Unique, professionally crafted design tailored to your business brand identity and industry standards
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-2xl border border-green-100">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-pages-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">3-5 Custom Pages</h3>
              <p className="text-gray-600 leading-relaxed">
                Home, About, Services, Contact, and one additional page of your choice to showcase your business
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-white p-8 rounded-2xl border border-purple-100">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-smartphone-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Mobile Responsive</h3>
              <p className="text-gray-600 leading-relaxed">
                Flawless display and functionality across all devices - smartphones, tablets, and desktops
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-white p-8 rounded-2xl border border-orange-100">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-search-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Advanced SEO Optimization</h3>
              <p className="text-gray-600 leading-relaxed">
                Comprehensive on-page SEO including meta tags, schema markup, and optimized content for search engines
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-2xl border border-teal-100">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-mail-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Contact Forms</h3>
              <p className="text-gray-600 leading-relaxed">
                Professional contact forms with email notifications to capture and manage customer inquiries
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-white p-8 rounded-2xl border border-yellow-100">
              <div className="w-14 h-14 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-map-pin-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Google Maps Integration</h3>
              <p className="text-gray-600 leading-relaxed">
                Embedded Google Maps to help customers find your business location easily
              </p>
            </div>

            <div className="bg-gradient-to-br from-indigo-50 to-white p-8 rounded-2xl border border-indigo-100">
              <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-share-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Social Media Integration</h3>
              <p className="text-gray-600 leading-relaxed">
                Connect your social media profiles and enable easy sharing of your content
              </p>
            </div>

            <div className="bg-gradient-to-br from-pink-50 to-white p-8 rounded-2xl border border-pink-100">
              <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-image-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Professional Images</h3>
              <p className="text-gray-600 leading-relaxed">
                High-quality stock images or integration of your provided photos and graphics
              </p>
            </div>

            <div className="bg-gradient-to-br from-cyan-50 to-white p-8 rounded-2xl border border-cyan-100">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">60 Days Support</h3>
              <p className="text-gray-600 leading-relaxed">
                Two months of technical support and minor updates after launch to ensure smooth operation
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Our Business Website Design</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional websites that help your business grow and succeed online
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center">
                  <i className="ri-trophy-line text-2xl text-white"></i>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Professional Brand Image</h3>
                <p className="text-gray-600 leading-relaxed">
                  Establish credibility and trust with a polished, professional website that reflects your business values and expertise in your industry.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                  <i className="ri-line-chart-line text-2xl text-white"></i>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Increased Conversions</h3>
                <p className="text-gray-600 leading-relaxed">
                  Strategic design and clear calls-to-action guide visitors toward becoming customers, maximizing your return on investment.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                  <i className="ri-customer-service-line text-2xl text-white"></i>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">24/7 Business Presence</h3>
                <p className="text-gray-600 leading-relaxed">
                  Your website works around the clock, providing information and capturing leads even when your office is closed.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                  <i className="ri-global-line text-2xl text-white"></i>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Reach More Customers</h3>
                <p className="text-gray-600 leading-relaxed">
                  Expand your market reach beyond local boundaries and attract customers from across Canada, USA, and worldwide.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-500 to-cyan-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Elevate Your Business Online?</h2>
          <p className="text-xl text-white/90 mb-8">
            Get a professional business website that drives results. Contact us today for a free consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <a
              href="/contact"
              className="bg-white text-teal-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              Book Free Consultation
            </a>
            <a
              href="/contact"
              className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white/20 transition-all duration-300 border-2 border-white cursor-pointer whitespace-nowrap"
            >
              View Our Work
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
